import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import { useAuth } from '../hooks/useAuth';
import { apiFetch, setToken } from '../lib/api';

const BRAND_BLUE = '#1E40AF';
const BRAND_CYAN = '#06B6D4';
// Use the ADMIN-labeled icon when we're running inside the Capacitor admin app;
// the public web (daysheets.app in a browser) keeps the general brand icon.
const LOGO_SRC = Capacitor.isNativePlatform() ? '/icon-admin.png' : '/icon.png';

export default function LoginPage() {
  const { login, register, loginWithGoogle, user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Top-level picker: which input surface is visible
  // 'choose' — the iOS-style Google + access code landing
  // 'email'  — email sign-in / sign-up form
  // 'forgot' — reset link request
  // 'reset'  — set new password (from email link)
  const [mode,        setMode]        = useState('choose');
  const [emailMode,   setEmailMode]   = useState('login'); // within 'email': login|register
  const [email,       setEmail]       = useState('');
  const [password,    setPassword]    = useState('');
  const [firstName,   setFirstName]   = useState('');
  const [lastName,    setLastName]    = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [resetToken,  setResetToken]  = useState('');
  const [accessCode,  setAccessCode]  = useState('');

  const [loading,     setLoading]     = useState(false);
  const [error,       setError]       = useState('');
  const [success,     setSuccess]     = useState('');

  const redirect = (u) => {
    const redirectTo = searchParams.get('redirect');
    if (redirectTo) { navigate(redirectTo, { replace: true }); return; }
    if (!u.linkedArtistIds?.length && !u.isFirstUser) {
      navigate('/onboarding', { replace: true });
    } else if (u.role === 'admin') {
      navigate('/admin', { replace: true });
    } else {
      navigate('/viewer', { replace: true });
    }
  };

  // Google OAuth callback
  useEffect(() => {
    const tokenParam = searchParams.get('token');
    const err        = searchParams.get('error');
    if (tokenParam && window.location.pathname.includes('callback')) {
      setToken(tokenParam);
      apiFetch('/auth/me').then(d => {
        if (d.user) {
          localStorage.setItem('ds_user', JSON.stringify(d.user));
          window.dispatchEvent(new Event('daysheets:login'));
          const dest = (d.user.role === 'admin') ? '/admin'
            : (!d.user.linkedArtistIds?.length) ? '/onboarding' : '/viewer';
          window.location.replace(dest);
        } else setError('Google sign-in failed.');
      }).catch(() => setError('Google sign-in failed.'));
      return;
    }
    if (err) setError('Google sign-in failed. Please try again.');
  }, []);

  // Reset-password token in URL
  useEffect(() => {
    const t = searchParams.get('token');
    if (window.location.pathname.includes('reset-password') && t) {
      setResetToken(t);
      setMode('reset');
    }
  }, []);

  useEffect(() => { if (user) redirect(user); }, [user]);

  // ── Handlers ────────────────────────────────────────────────────────────────

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true); setError('');
    try { redirect(await login(email, password)); }
    catch(err) { setError(err.message); }
    finally { setLoading(false); }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    if (!firstName.trim()) { setError('First name is required'); return; }
    if (password.length < 6) { setError('Password must be at least 6 characters'); return; }
    setLoading(true); setError('');
    try { redirect(await register(firstName, lastName, email, password)); }
    catch(err) { setError(err.message); }
    finally { setLoading(false); }
  };

  const handleForgot = async (e) => {
    e.preventDefault();
    setLoading(true); setError(''); setSuccess('');
    try {
      await apiFetch('/auth/forgot-password', { method:'POST', body: JSON.stringify({ email }) });
      setSuccess('If an account exists, a reset link has been sent.');
    } catch(err) { setError(err.message); }
    finally { setLoading(false); }
  };

  const handleReset = async (e) => {
    e.preventDefault();
    if (newPassword.length < 6) { setError('Password must be at least 6 characters'); return; }
    setLoading(true); setError('');
    try {
      const data = await apiFetch('/auth/reset-password', { method:'POST', body: JSON.stringify({ token: resetToken, password: newPassword }) });
      setToken(data.token);
      redirect(data.user);
    } catch(err) { setError(err.message); }
    finally { setLoading(false); }
  };

  const handleAccessCode = async () => {
    const normalized = accessCode
      .replace(/\u2013|\u2014|\u2212/g, '-')
      .replace(/\s+/g, '')
      .trim();
    if (normalized.length < 4) return;
    setLoading(true); setError('');
    try {
      const data = await apiFetch('/auth/access-code', { method:'POST', body: JSON.stringify({ code: normalized }) });
      if (data.token) {
        setToken(data.token);
        localStorage.setItem('ds_user', JSON.stringify(data.user));
        window.dispatchEvent(new Event('daysheets:login'));
        redirect(data.user);
      }
    } catch(err) { setError('Invalid code. Check with your tour manager.'); }
    finally { setLoading(false); }
  };

  // ── UI ──────────────────────────────────────────────────────────────────────

  const card = {
    background:'var(--card)', border:'1px solid var(--border)',
    borderRadius:16, padding:'14px 16px',
    fontSize:15, color:'var(--text)', outline:'none',
    fontFamily:'inherit', width:'100%', boxSizing:'border-box',
  };
  const primaryBtn = {
    width:'100%', display:'flex', alignItems:'center', justifyContent:'center', gap:10,
    background: BRAND_CYAN, color:'#fff', border:'none', borderRadius:12,
    padding:'14px', fontSize:16, fontWeight:700, cursor:'pointer', fontFamily:'inherit',
    boxShadow:'0 4px 14px rgba(6,182,212,0.3)', transition:'all 0.15s',
  };
  const googleBtn = {
    width:'100%', display:'flex', alignItems:'center', justifyContent:'center', gap:12,
    background:'#fff', border:'1px solid #DDD', borderRadius:12,
    padding:'14px', fontSize:15, fontWeight:500, color:'#3c4043',
    cursor:'pointer', fontFamily:'inherit', transition:'all 0.15s',
  };

  return (
    <div style={{ minHeight:'100vh', background:'var(--bg)', display:'flex', alignItems:'center', justifyContent:'center', padding:24 }}>
      <div style={{ width:'100%', maxWidth:400, display:'flex', flexDirection:'column', gap:24 }}>

        {/* ── Brand lockup ─────────────────────────────────────────────────── */}
        <div style={{ display:'flex', flexDirection:'column', alignItems:'center', gap:14, marginTop:24 }}>
          <img src={LOGO_SRC} alt="Day Sheets"
            style={{ width:96, height:96, borderRadius:22, boxShadow:`0 8px 24px ${BRAND_BLUE}33` }} />
          <div style={{ textAlign:'center' }}>
            <div style={{ fontSize:36, fontWeight:900, color: BRAND_BLUE, letterSpacing:-1.2, lineHeight:1, fontFamily:'-apple-system, BlinkMacSystemFont, "SF Pro Rounded", sans-serif' }}>
              Day Sheets
            </div>
            <div style={{ marginTop:6, fontSize:14, fontWeight:500, color:'var(--text2)' }}>
              Calendar Simplified
            </div>
          </div>
        </div>

        {/* ── CHOOSE: Google + access code (iOS-style landing) ─────────────── */}
        {mode === 'choose' && (
          <>
            <button onClick={loginWithGoogle} style={googleBtn}>
              <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
                <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
                <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
                <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
                <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
              </svg>
              Sign in with Google
            </button>

            <div style={{ display:'flex', alignItems:'center', gap:12 }}>
              <div style={{ flex:1, height:1, background:'var(--border)' }} />
              <span style={{ fontSize:11, color:'var(--text3)', fontWeight:700, letterSpacing:1 }}>OR</span>
              <div style={{ flex:1, height:1, background:'var(--border)' }} />
            </div>

            <input
              value={accessCode}
              onChange={e => setAccessCode(e.target.value.toUpperCase())}
              onKeyDown={e => e.key === 'Enter' && handleAccessCode()}
              placeholder="Access code"
              maxLength={24}
              style={{ ...card, textAlign:'center', letterSpacing:3, fontFamily:'ui-monospace,Menlo,monospace', fontSize:16, fontWeight:700, textTransform:'uppercase' }}
            />
            <button onClick={handleAccessCode} disabled={loading || accessCode.trim().length < 4}
              style={{ ...primaryBtn, opacity: (loading || accessCode.trim().length < 4) ? 0.4 : 1 }}>
              {loading ? 'Joining…' : 'Join Day Sheet'}
            </button>

            <button onClick={() => { setError(''); setMode('email'); setEmailMode('login'); }}
              style={{ background:'none', border:'none', cursor:'pointer', color:'var(--text2)', fontSize:14, fontWeight:600, fontFamily:'inherit', padding:8 }}>
              Sign in with email
            </button>
          </>
        )}

        {/* ── EMAIL: login / register form ─────────────────────────────────── */}
        {mode === 'email' && (
          <>
            <div style={{ display:'flex', background:'var(--card)', border:'1px solid var(--border)', borderRadius:12, padding:4 }}>
              {['login','register'].map(m => (
                <button key={m} onClick={() => { setEmailMode(m); setError(''); }}
                  style={{ flex:1, padding:'10px 14px', borderRadius:8, background: emailMode===m ? BRAND_BLUE : 'transparent',
                    color: emailMode===m ? '#fff' : 'var(--text2)', border:'none', cursor:'pointer', fontFamily:'inherit',
                    fontSize:14, fontWeight:700 }}>
                  {m === 'login' ? 'Sign In' : 'Sign Up'}
                </button>
              ))}
            </div>

            {emailMode === 'login' ? (
              <form onSubmit={handleLogin} style={{ display:'flex', flexDirection:'column', gap:12 }}>
                <input value={email} onChange={e=>setEmail(e.target.value)} placeholder="Email" type="email" required autoFocus style={card} />
                <input value={password} onChange={e=>setPassword(e.target.value)} placeholder="Password" type="password" required style={card} />
                <button type="button" onClick={() => { setMode('forgot'); setError(''); setSuccess(''); }}
                  style={{ background:'none', border:'none', cursor:'pointer', color: BRAND_BLUE, fontSize:12, textAlign:'right', padding:0, fontFamily:'inherit', fontWeight:700 }}>
                  Forgot password?
                </button>
                <button type="submit" disabled={loading} style={{ ...primaryBtn, background: BRAND_BLUE, boxShadow:`0 4px 14px ${BRAND_BLUE}4d`, opacity: loading ? 0.7 : 1 }}>
                  {loading ? 'Signing in…' : 'Sign In'}
                </button>
              </form>
            ) : (
              <form onSubmit={handleRegister} style={{ display:'flex', flexDirection:'column', gap:12 }}>
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
                  <input value={firstName} onChange={e=>setFirstName(e.target.value)} placeholder="First name" required autoFocus style={card} />
                  <input value={lastName} onChange={e=>setLastName(e.target.value)} placeholder="Last name" style={card} />
                </div>
                <input value={email} onChange={e=>setEmail(e.target.value)} placeholder="Email" type="email" required style={card} />
                <input value={password} onChange={e=>setPassword(e.target.value)} placeholder="Password (min 6)" type="password" required style={card} />
                <button type="submit" disabled={loading} style={{ ...primaryBtn, background: BRAND_BLUE, boxShadow:`0 4px 14px ${BRAND_BLUE}4d`, opacity: loading ? 0.7 : 1 }}>
                  {loading ? 'Creating…' : 'Create Account'}
                </button>
              </form>
            )}

            <button onClick={() => { setMode('choose'); setError(''); }}
              style={{ background:'none', border:'none', cursor:'pointer', color:'var(--text2)', fontSize:14, fontWeight:600, fontFamily:'inherit', padding:8 }}>
              ← Back
            </button>
          </>
        )}

        {/* ── FORGOT ───────────────────────────────────────────────────────── */}
        {mode === 'forgot' && (
          <form onSubmit={handleForgot} style={{ display:'flex', flexDirection:'column', gap:12 }}>
            <input value={email} onChange={e=>setEmail(e.target.value)} placeholder="Email" type="email" required autoFocus style={card} />
            <button type="submit" disabled={loading} style={{ ...primaryBtn, background: BRAND_BLUE, boxShadow:`0 4px 14px ${BRAND_BLUE}4d`, opacity: loading ? 0.7 : 1 }}>
              {loading ? 'Sending…' : 'Send Reset Link'}
            </button>
            <button type="button" onClick={() => setMode('email')}
              style={{ background:'none', border:'none', cursor:'pointer', color:'var(--text2)', fontSize:14, fontWeight:600, fontFamily:'inherit', padding:8 }}>
              ← Back
            </button>
          </form>
        )}

        {/* ── RESET ────────────────────────────────────────────────────────── */}
        {mode === 'reset' && (
          <form onSubmit={handleReset} style={{ display:'flex', flexDirection:'column', gap:12 }}>
            <input value={newPassword} onChange={e=>setNewPassword(e.target.value)} placeholder="New password (min 6)" type="password" required autoFocus style={card} />
            <button type="submit" disabled={loading} style={{ ...primaryBtn, background: BRAND_BLUE, boxShadow:`0 4px 14px ${BRAND_BLUE}4d`, opacity: loading ? 0.7 : 1 }}>
              {loading ? 'Saving…' : 'Set New Password'}
            </button>
          </form>
        )}

        {/* ── Messages ─────────────────────────────────────────────────────── */}
        {error && (
          <div style={{ padding:'10px 14px', background:'rgba(248,81,73,0.12)', border:'1px solid rgba(248,81,73,0.3)', borderRadius:10, fontSize:13, color:'var(--red)', textAlign:'center' }}>
            {error}
          </div>
        )}
        {success && (
          <div style={{ padding:'10px 14px', background:'rgba(63,185,80,0.1)', border:'1px solid rgba(63,185,80,0.3)', borderRadius:10, fontSize:13, color:'#3fb950', textAlign:'center' }}>
            {success}
          </div>
        )}

        <p style={{ textAlign:'center', fontSize:12, color:'var(--text3)', lineHeight:1.5, marginTop:'auto' }}>
          Manager? Sign in with Google. Crew &amp; family? Use your access code.
        </p>
      </div>
    </div>
  );
}
